import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { listNewsletterSubscribers } from "@/lib/admin/newsletter.functions";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminDataToolbar } from "@/components/admin/import/AdminDataToolbar";
import { Pager } from "@/components/admin/Pager";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export const Route = createFileRoute("/_admin/admin/newsletter")({
  component: NewsletterPage,
});

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

function statusVariant(status: string): "default" | "secondary" | "outline" | "destructive" {
  if (status === "active") return "default";
  if (status === "unsubscribed") return "secondary";
  if (status === "bounced") return "destructive";
  return "outline";
}

function NewsletterPage() {
  const [page, setPage] = useState(1);
  const queryClient = useQueryClient();
  const list = useServerFn(listNewsletterSubscribers);
  const { data, isLoading, isError } = useQuery({
    queryKey: ["admin", "newsletter", page],
    queryFn: () => list({ data: { page } }),
  });

  return (
    <div>
      <AdminPageHeader
        title="Newsletter subscribers"
        description="Newest first."
        actions={
          <AdminDataToolbar
            exportName="newsletter-subscribers"
            moduleId="newsletter"
            rows={() => (data?.rows ?? []).map((r) => ({ email: r.email, status: r.status, subscribed_at: r.created_at, unsubscribed_at: r.unsubscribed_at ?? "", resubscribed_at: r.resubscribed_at ?? "" }))}
            onImported={() =>
              queryClient.invalidateQueries({ queryKey: ["admin", "newsletter"] })
            }
          />
        }
      />
      {isError ? (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>
            Couldn't load subscribers. Please refresh and try again.
          </AlertDescription>
        </Alert>
      ) : null}
      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : data && data.rows.length > 0 ? (
        <>
          <div className="hidden overflow-hidden rounded-md border md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Subscribed</TableHead>
                  <TableHead>Unsubscribed</TableHead>
                  <TableHead>Resubscribed</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-medium">{r.email}</TableCell>
                    <TableCell>
                      <Badge variant={statusVariant(r.status)}>{r.status}</Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {formatDate(r.created_at)}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {formatDate(r.unsubscribed_at)}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {formatDate(r.resubscribed_at)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <ul className="space-y-3 md:hidden">
            {data.rows.map((r) => (
              <li key={r.id} className="rounded-md border p-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-foreground">{r.email}</span>
                  <Badge variant={statusVariant(r.status)}>{r.status}</Badge>
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  Subscribed {formatDate(r.created_at)}
                </div>
                {r.unsubscribed_at ? (
                  <div className="text-xs text-muted-foreground">
                    Unsubscribed {formatDate(r.unsubscribed_at)}
                  </div>
                ) : null}
                {r.resubscribed_at ? (
                  <div className="text-xs text-muted-foreground">
                    Resubscribed {formatDate(r.resubscribed_at)}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
          <Pager
            page={data.page}
            pageSize={data.pageSize}
            total={data.total}
            onPageChange={setPage}
          />
        </>
      ) : (
        <p className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
          No subscribers yet.
        </p>
      )}
    </div>
  );
}